import {
	sBanner,
	sShowScreen,
	sInputKeyValidationHelper,
	sAutocompleteListSetup,
	sResetAutocomplete,
} from '../../shared.min.js';

const travelVaccinationsWidget = (function () {
	// Widget scoped vars
	let jsonData = null;

	const widget = document.getElementById('cnfsd-travel_vaccinations'),
		screens = widget.querySelectorAll('.widget__screen'),
		dataUrl = widget ? widget.getAttribute('data-json') : widget.getAttribute('data-json-path'),
		progressTitle1 = widget ? widget.getAttribute('data-progress-title-1') || '' : null,
		progressTitle2 = widget ? widget.getAttribute('data-progress-title-2') || '' : null,
		fieldGroup = widget ? widget.querySelector('.field-group') : null,
		autocompleteHTMLElement = widget ? widget.querySelector('#autocomplete') : null,
		autocompleteListHTMLElement = widget ? widget.querySelector('#sAutocompleteListElement') : null,
		autocompleteSelectedItemsContainer = widget ? widget.querySelector('#sAutocompleteSelected') : null,
		submitButton = widget ? widget.querySelector('#submit') : null,
		resultsDiv = widget ? widget.querySelector('#travel_vaccinations__results__content') : null,
		restartButton = widget ? widget.querySelector('.travel_vaccinations__reset') : null,
		pdfOuter = document.getElementById('travel_vaccinations__pdf'),
		pdfContainer = widget ? widget.querySelector('.travel_vaccinations__pdf__container') : null,
		downloadButton = document.getElementById('travel_vaccinations__download');
	

	async function init() {
		if (!widget) return;
		if (dataUrl || !dataUrl.trim() === '') {
			const data = await fetch(dataUrl);
			jsonData = await data.json();
		} else {
			jsonData = widget.getAttribute('data-json');
			jsonData = jsonData ? JSON.parse(jsonData) : null;
		}

		sBanner(
			widget,
			[progressTitle1, progressTitle2],
			'.banner-current-text',
			'.banner-current-progress',
			'.banner__progress-bar__bar',
			1
		);

		// hierarchical: regions → countries
		const { selectedItemsArray, selectedItemsContainer, autocompleteElement, autocompleteListElement } =
			sAutocompleteListSetup({
				data: jsonData,
				topKey: 'regions', // default value
				nestedKey: 'countries', // default value
				topLabelAttr: 'data-category', // default value
				itemLabelAttr: 'data-autocomplete-item',
				autocompleteElement: autocompleteHTMLElement, // pass in the autocomplete element
				autocompleteListElement: autocompleteListHTMLElement,
				selectType: 'multiple',
				maxSelectedItems: 4, // allow multiple selections
				selectedItemsContainer: autocompleteSelectedItemsContainer,
				selectedItemsContainerTitle: 'Your travel destinations',
			});

		eventHandlers(selectedItemsArray, selectedItemsContainer, autocompleteElement, autocompleteListElement);
	}

	const eventHandlers = (
		selectedItemsArray,
		selectedItemsContainer,
		autocompleteElement,
		autocompleteListElement
	) => {
		submitButton.addEventListener('click', function (e) {
			e.preventDefault();
			if (!isCountrySelected(selectedItemsArray)) {
				fieldGroup.classList.add('field-group--error');
				return;
			}
			sShowScreen(screens, 2);
			resetResultsData();
			getSelectedCountries(selectedItemsArray);
		});

		restartButton.addEventListener('click', function (e) {
			e.preventDefault();
			sShowScreen(screens, 1);
			resetResultsData();
			sResetAutocomplete(
				selectedItemsArray,
				selectedItemsContainer,
				autocompleteElement,
				autocompleteListElement
			);
			
			const banner = widget.querySelector('.banner');
			if (banner) {
				banner.scrollIntoView({ behavior: 'smooth', block: 'start', inline: "nearest" });
			}

			pdfContainer.innerHTML = ''; // Clear previous PDF content
		});

		downloadButton.addEventListener('click', function (e) {
			e.preventDefault();
			if (!isCountrySelected(selectedItemsArray)) {
				fieldGroup.classList.add('field-group--error');
				return;
			}
			generatePDF(pdfOuter);
		});
	};

	const resetResultsData = () => {
		resultsDiv.innerHTML = ''; // Clear previous results
	};

	const isCountrySelected = selectedItemsArray => {
		if (selectedItemsArray.length === 0) {
			fieldGroup.classList.add('field-group--error');
			console.error('No countries selected. Please select at least one country.');
			return false;
		} else {
			fieldGroup.classList.remove('field-group--error');
		}
		return true;
	};

	const getSelectedCountries = selectedItemsArray => {
		if (!selectedItemsArray || selectedItemsArray.length === 0) {
			console.error('No countries selected. Please select at least one country.');
			return;
		}

		selectedItemsArray.forEach(place => {
			
			const countryHTML = getCountryHTML(place);
			if (countryHTML) {
				const countryDiv = document.createElement('div');
				countryDiv.classList.add('travel_vaccinations__results__country');
				
				// Safely parse the HTML string and append the content
				const parser = new DOMParser();
				const parsedDoc = parser.parseFromString(countryHTML, 'text/html');

                // Append the parsed elements from the body of the new document
                // to the countryDiv. This avoids executing scripts.
                while (parsedDoc.body.firstChild) {
                    countryDiv.appendChild(parsedDoc.body.firstChild);
                }
                
                resultsDiv.appendChild(countryDiv);
                pdfContainer.appendChild(countryDiv.cloneNode(true)); // Clone for PDF
                stylePDFElement(pdfOuter);
            } else {
                console.error(`No data found for ${place}`);
            }
        });
    };

    // Handle the aliases for vaccines - handles slight variations in naming
	// and combines courses that are given as a single vaccine
	const alias = Object.freeze({
		polio: 'Polio, diphtheria and tetanus combined',
		diphtheria: 'Polio, diphtheria and tetanus combined',
		tetanus: 'Polio, diphtheria and tetanus combined',
		measles: 'Measles, Mumps, Rubella (MMR)',
		mumps: 'Measles, Mumps, Rubella (MMR)',
		rubella: 'Measles, Mumps, Rubella (MMR)',
		'rabies (bat lyssavirus)': 'Rabies',
		rabies: 'Rabies',
    });

    const displayNames = Object.freeze({
        'Polio, diphtheria and tetanus combined': 'Given as one vaccine (Td/IPV) that protects against polio, diphtheria, and tetanus.',
        'Measles, Mumps, Rubella (MMR)': 'Given as one vaccine (MMR) that protects against measles, mumps, and rubella.'
    });

    const combinedCourses = ['Polio, diphtheria and tetanus combined', 'Measles, Mumps, Rubella (MMR)'];

	// normalise a string so slight spelling / spacing differences don’t break the equality test
	const normalise = s => s.toLowerCase().replace(/[^a-z0-9]/g, '');

	function findVaccines(vaccine) {
		const vaccineToMatch = normalise(vaccine);

		// 1) exact
		let matchedVaccine = jsonData.vaccines.filter(v => normalise(v.vaccination) === vaccineToMatch);
		if (matchedVaccine.length) return matchedVaccine;

		// 2) alias
		const aliasName = alias[vaccineToMatch];
		if (aliasName) {
			const vaccineToMatchAlias = normalise(aliasName);
			matchedVaccine = jsonData.vaccines.filter(v => normalise(v.vaccination) === vaccineToMatchAlias);
			if (matchedVaccine.length) return matchedVaccine;
		}

		// 3) partial (last-chance) – either side contains the other
		return jsonData.vaccines.filter(v => {
			const norm = normalise(v.vaccination);
			return norm.includes(vaccineToMatch) || vaccineToMatch.includes(norm);
		});
	}

	function renderRequirement(vaccine) {
        const matches = findVaccines(vaccine);
        if (!matches.length) return ''; // nothing to show

        const first = matches[0];
        // Use the custom display name if it exists, otherwise use the name from the data.
        // .trim() is used to remove whitespace, ensuring the lookup is reliable.
        const displayName = displayNames[first.vaccination.trim()] || first.vaccination;
        const courseName = escapeHTML(displayName);

        const showCourse =
            combinedCourses.some(c => normalise(c) === normalise(first.vaccination)) &&
            normalise(first.vaccination) !== normalise(vaccine); // avoid duplicates

        return `
            <div class="vaccine">
            <h3 class="vaccine__title">${escapeHTML(vaccine)}</h3>
            ${showCourse ? `<p class="vaccine__course">${courseName}</p>` : ''}
        
            <details class="mod-details-summary__item">
                <summary>
                <h4 class="mod-details-summary__question">Details</h4>
                </summary>
                <div class="mod-details-summary__content">
                <p><span class="vaccine__advice__title">Price per dose at Boots (GBP):</span> <span  class="vaccine__advice__value">£${escapeHTML(first.priceGBP)}</span></p>
                <p><span class="vaccine__advice__title">Available on the NHS:</span> <span  class="vaccine__advice__value">${escapeHTML(first.nhsFree)}</span></p>
                <p><span class="vaccine__advice__title">Amount of doses per course:</span> <span  class="vaccine__advice__value">${escapeHTML(first.dosesPerCourse)}</span></p>
                <p><span class="vaccine__advice__title">Recommended timeframe before travel:</span>
					<span  class="vaccine__advice__value">${escapeHTML(first.recommendedTimeframeBeforeTravel)}</span></p>
				</div>
			</details>
			</div>`;
	}

	function escapeHTML(str) {
		if (typeof str !== 'string') return str;
		return str
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#039;');
    }

    function decodeHTMLEntities(text) {
        if (typeof text !== 'string') return text;
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        return doc.documentElement.textContent;
    }

    function buildSection(country, prop, heading, intro) {
		const list = country[prop] ?? [];
		if (!list.length || list.every(x => x === 'No')) return '';

		const items = list
			.map(renderRequirement) // one card per requirement
			.filter(Boolean) // drop blanks from findVaccines(req)
			.join('');

		return items
			? `<details class="mod-details-accordion__item">
			   <summary><h3 class="mod-details-accordion__question">${escapeHTML(heading)}</h3></summary>
			   <div class="mod-details-accordion__content">
				 <h2 class="mod-details-accordion__content__title">${escapeHTML(intro)}</h2>
				 <div class="mod-tool-tip mod-tool-tip--no-icon mod-tool-tip--important-info">
				 	<div class="mod-tool-tip__content">
				 		<div class="mod-tool-tip__content__title">
							<span>
								Yellow fever certificate required if arriving from or transiting through a country with yellow fever risk. Proof of polio vaccination may be required if arriving from a country with polio risk. Check with your GP for personalised advice.
								${
									country.source && (country.source.startsWith('http:') || country.source.startsWith('https:'))
										? `<a href="${escapeHTML(country.source)}">
										Learn more
									</a>`
										: ''
								}
							</span>
						</div>
					</div>
				</div>
				 ${items}
			   </div>
			 </details>
			 `
			: '';
	}

	function buildCombinedSection(country, heading, groups) {
		/* groups: [ { prop:'recommended',    intro:'…' },
					 { prop:'someTravellers', intro:'…' } ]            */

		const parts = groups.flatMap(({ prop, intro, additionalHTML }) => {
			const list = country[prop] ?? [];
			if (!list.length || list.every(x => x === 'No')) return [];

			const items = list
				.map(renderRequirement) // one card per requirement, no dedupe
				.filter(Boolean) // drop unresolved requirements
				.join('');

			return items
				? [
						`<h2 class="mod-details-accordion__content__title">${intro}</h2>
					${additionalHTML}
					${items}`,
					]
				: [];
		});

		if (!parts.length) return ''; // nothing to show at all

		return `
		  <details class="mod-details-accordion__item">
			<summary>
			  <h3 class="mod-details-accordion__question">${heading}</h3>
			</summary>
			<div class="mod-details-accordion__content">
			  ${parts.length > 0 ? parts.join('<hr class="mod-details-accordion__content__divider">') : parts.join('')}
			</div>
		  </details>`;
	}

	function buildTravelAdviceLink(country) {
        //get fcdo advice link from the json data
        const fcdoAdviceLink = escapeHTML(country.fcdo || '');
        const decodedCountryName = decodeHTMLEntities(country.name);

        return `
            <div class="vaccinations__travel-advice">
                <p class="vaccinations__travel-advice__text">
                    <a href="${fcdoAdviceLink}" class="vaccinations__travel-advice__link" target="_blank" rel="noopener noreferrer">
                        Check the latest travel advice for <b>${escapeHTML(decodedCountryName)}</b>	
                    </a>
                </p>
            </div>

            `;
    }

    function getCountryHTML(countryName) {

        const country = jsonData.regions
            .flatMap(r => r.countries)
            .find(c => {
                return normalise(decodeHTMLEntities(c.name)) === normalise(countryName);
            });

        if (!country) {
            console.error(`Country "${escapeHTML(countryName)}" not found`);
            return null;
        }

        // Decode the name for display purposes AFTER finding the country object
        const decodedCountryName = decodeHTMLEntities(country.name);

        // Pass in country, section we are building for, heading for the section, and intro text
        const required = buildSection(
            country,
            'certificateRequirements',
            'Required vaccines',
            'All travellers require*'
        );

		const recommended = buildCombinedSection(
			country,
			'Recommended vaccines', [
            {
                prop: 'recommended',
                intro: 'All travellers are recommended*',
                additionalHTML: '',
            },
            {
                prop: 'someTravellers',
                intro: 'Some travellers are recommended*',
                additionalHTML: `
					<div class="mod-tool-tip mod-tool-tip--no-icon mod-tool-tip--important-info">
						<div class="mod-tool-tip__content">
						<div class="mod-tool-tip__content__title">
							<span>
							Some travellers may need extra vaccines due to their job, lifestyle, or health.
							${
								country.source && (country.source.startsWith('http:') || country.source.startsWith('https:'))
									? `<a href="${escapeHTML(country.source)}">
									Find out who might need extra vaccines
								</a>`
									: ''
							}
							</span>
						</div>
						</div>
					</div>`,
            },
        ]);

        const travelAdviceLink = buildTravelAdviceLink(country);

        return `<div class="mod-details-accordion">
              <h2 class="accordion-title">${escapeHTML(decodedCountryName)}</h2>
              ${required}
              ${recommended}
              ${travelAdviceLink}
			  <div class="mod-details-accordion__cta">
			  	Don't forget to compare your travel insurance quotes with 
				<svg xmlns="http://www.w3.org/2000/svg" width="119" height="22" fill="none"><path d="M107.109 9.795v-.672c0-.778-.296-1.172-.851-1.172-.555 0-.86.394-.86 1.172v.672c0 .777.307 1.184.86 1.184.553 0 .851-.403.851-1.184z" fill="#1F1F1F"/><path fill-rule="evenodd" clip-rule="evenodd" d="M115.094 15.945a5.81 5.81 0 0 0 3.524-3.688c.454-2.678.504-5.409.148-8.101a4.456 4.456 0 0 0-3.499-3.459c-2.441-.54-10.933 1.536-13.352 2.441-1.828.682-4.379 2.013-4.682 5.815a14.383 14.383 0 0 0 .222 3.904c.403 1.554 1.719 2.963 4.625 3.362 3.359.461 9.996.699 13.016-.276l-.002.002zm-12.188-7.931a8.735 8.735 0 0 0-1.165-.068c-.429 0-.783.258-.783 1.114v.808c0 .853.354 1.11.783 1.11.385-.002.769-.023 1.152-.064a.181.181 0 0 1 .21.148v.008c.017.113.1.74.112.853.013.114-.057.185-.2.222a6.447 6.447 0 0 1-1.38.142A2.144 2.144 0 0 1 99.41 9.86v-.807a2.14 2.14 0 0 1 2.226-2.429c.463-.002.926.046 1.379.145.144.037.222.105.201.221-.011.06-.034.25-.057.438-.023.184-.045.365-.055.418a.16.16 0 0 1-.046.116.163.163 0 0 1-.152.047v.005zm5.742 1.781a2.391 2.391 0 0 1-4.007 1.87 2.39 2.39 0 0 1-.775-1.66v-.882a2.396 2.396 0 0 1 2.286-2.496 2.395 2.395 0 0 1 2.496 2.496v.672zm8.218 2.21a.21.21 0 0 1-.201.2h-1.152a.203.203 0 0 1-.193-.2V8.727c0-.614-.142-.796-.583-.796a1.84 1.84 0 0 0-.74.173c.019.19.03.412.03.623v3.276a.206.206 0 0 1-.193.201h-1.156a.212.212 0 0 1-.139-.062.213.213 0 0 1-.06-.139V8.728c0-.614-.114-.796-.552-.796-.258.03-.506.115-.727.25v3.822a.203.203 0 0 1-.191.2h-1.153a.208.208 0 0 1-.202-.2v-5.11a.193.193 0 0 1 .193-.192h1.087c.051 0 .099.021.135.057a.194.194 0 0 1 .055.135v.207a2.53 2.53 0 0 1 1.413-.478 1.341 1.341 0 0 1 1.128.469 2.801 2.801 0 0 1 1.553-.47c1.258 0 1.64.847 1.64 2.103l.008 3.28z" fill="#1F1F1F"/><path d="M98.855 19.152c.04-.57-.1-1.14-.4-1.628a2.448 2.448 0 0 0-2.094-.74h-.655a2.45 2.45 0 0 0-2.094.74 2.683 2.683 0 0 0-.4 1.628c-.05.595.09 1.191.4 1.702a2.446 2.446 0 0 0 2.094.74h.655a2.448 2.448 0 0 0 2.095-.74 2.837 2.837 0 0 0 .4-1.702zM66.912 18.047c0 1.768-1.209 3.835-4.587 3.835h-.002a18.934 18.934 0 0 1-4.343-.539.697.697 0 0 1-.444-.87c.068-.288.282-1.047.38-1.395l.024-.084a.692.692 0 0 1 .786-.448c1.122.229 2.26.369 3.404.418 1.072 0 1.313-.348 1.313-.788 0-.211-.125-.584-.802-1.002l-.744-.46c-.626-.387-1.419-.878-2.335-1.442a3.52 3.52 0 0 1-1.976-3.06c0-2.458 1.58-3.815 4.452-3.815 1.393.012 2.776.23 4.106.645l.012.004a.723.723 0 0 1 .456.915c-.11.46-.195.759-.31 1.145a.712.712 0 0 1-.837.56 23.351 23.351 0 0 0-3.18-.348c-.846 0-1.274.328-1.274.698 0 .423.206.574.744.867.538.293 1.139.616 2.687 1.445a3.98 3.98 0 0 1 2.47 3.719zM10.134 21.37a20.09 20.09 0 0 1-3.625.4l-.004.006C4.991 21.776 0 21.269 0 14.723v-3.269c0-6.366 4.55-7.053 6.505-7.053a16.17 16.17 0 0 1 3.784.46.698.698 0 0 1 .485.814c-.076.262-.16.567-.242.87-.07.256-.139.51-.202.733a.695.695 0 0 1-.745.452 33.108 33.108 0 0 0-3.037-.168c-2.005 0-2.939 1.234-2.939 3.889v3.262c0 2.618.96 3.892 2.939 3.892.674 0 1.496-.06 2.155-.109.382-.027.709-.051.921-.057a.758.758 0 0 1 .794.666c.057.202.279 1.012.37 1.371a.744.744 0 0 1-.655.894zM52.64 21.362a.702.702 0 0 1-.177-.506l.002-.007a5.696 5.696 0 0 1-3.207.897c-3.212 0-4.077-2.056-4.077-5.23V9.268a.709.709 0 0 1 .7-.717h2.031a.724.724 0 0 1 .701.727v7.237c0 1.739.217 2.268 1.618 2.268a5.72 5.72 0 0 0 2.234-.783V9.245a.694.694 0 0 1 .687-.692h2.044a.712.712 0 0 1 .7.715v11.64a.692.692 0 0 1-.7.687h-2.071a.702.702 0 0 1-.484-.233zM41.04 21.54a.683.683 0 0 1-.263.049l-2.047-.002a.694.694 0 0 1-.685-.7V11.27s-1.367-.23-1.788-.308a.693.693 0 0 1-.617-.674V9.253a.703.703 0 0 1 .7-.704h1.702v-.83c0-3.36 2.062-3.811 4.078-3.811a9.806 9.806 0 0 1 1.732.2l.04.011a.71.71 0 0 1 .507.867l-.08.3c-.082.313-.17.647-.225.834a.713.713 0 0 1-.695.526s-.679-.041-.88-.041c-.916 0-1.043.206-1.043 1.071v.871h1.815a.7.7 0 0 1 .7.704v1.35a.684.684 0 0 1-.701.665h-1.82v9.648a.685.685 0 0 1-.43.627zM34.295 21.54a.691.691 0 0 1-.266.049l-2.03.004h-.025a.702.702 0 0 1-.678-.728v-7.237c0-1.855-.13-2.225-1.615-2.225a5.05 5.05 0 0 0-2.234.696v8.765a.725.725 0 0 1-.703.728h-2.067a.695.695 0 0 1-.659-.728V9.254a.704.704 0 0 1 .7-.704h1.924a.7.7 0 0 1 .697.704c.998-.6 2.15-.9 3.314-.86 3.593 0 4.077 2.644 4.077 5.233v7.278a.692.692 0 0 1-.435.634z" fill="#1F1F1F"/><path fill-rule="evenodd" clip-rule="evenodd" d="M89.614 3.66c.344-.062.665-.12.834-.149v.012a.692.692 0 0 1 .8.695v16.115c0 .54-.277.716-.79.851a18.913 18.913 0 0 1-4.502.586c-3.567 0-5.533-2.07-5.533-5.831v-1.717c0-3.762 1.966-5.833 5.533-5.833.622.005 1.243.045 1.86.12V4.571a.71.71 0 0 1 .592-.7c.224-.037.736-.129 1.206-.213zm-3.659 15.195c.622.002 1.243-.036 1.86-.113v-7.298a18.44 18.44 0 0 0-1.86-.122c-1.492 0-2.102.845-2.102 2.912v1.709c0 2.068.61 2.912 2.102 2.912zM11.517 14.184c0-3.57 2.168-5.789 5.533-5.789v.003c3.367 0 5.534 2.218 5.534 5.789v1.795c0 3.567-2.123 5.787-5.534 5.787-3.411 0-5.533-2.217-5.533-5.787v-1.798zm5.533 4.537c1.337 0 2.102-.998 2.102-2.737v-1.797c0-1.742-.768-2.738-2.102-2.738-1.334 0-2.1.998-2.1 2.738v1.797c0 1.742.763 2.737 2.1 2.737zM68.044 14.296c0-3.69 2.154-5.9 5.557-5.9h.001c3.405 0 5.513 2.262 5.513 5.9v1.361a.703.703 0 0 1-.703.696h-6.93a2.21 2.21 0 0 0 2.384 2.368 22.998 22.998 0 0 0 3.673-.444.645.645 0 0 1 .705.5c.068.26.298 1.12.36 1.342a.694.694 0 0 1-.46.851c-1.459.465-2.972.736-4.5.806-2.583 0-5.6-1.526-5.6-5.831v-1.65zm3.93-2.085c-.354.43-.523.981-.47 1.534l4.151-.002c-.055-1.42-.834-2.293-2.053-2.293a2.097 2.097 0 0 0-1.629.762z" fill="#1F1F1F"/></svg>
			  </div>
            </div>`;
    }

    const stylePDFElement = pdfOuter => {
		const allDetails = pdfOuter.querySelectorAll('details');
		allDetails.forEach(detail => {
			// set all details elements to open
			detail.setAttribute('open', 'true');
		});
	};

	const generatePDF = async pdfOuter => {
		const pdfOptions = {
			margin: 10,
			filename: 'Confused.com_Vaccinations.pdf',
			image: { type: 'jpeg', quality: 0.98 },
			html2canvas: { scale: 2 },
			jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
			pagebreak: {
				mode: ['css'], // “avoid-all” keeps every element intact
			},
		};

		const element = pdfOuter;
		html2pdf(element, pdfOptions)	};

	return {
		init: init,
	};
})();

document.addEventListener('DOMContentLoaded', function () {
	travelVaccinationsWidget.init();
});
